import React from "react";
import { ViewModes } from "models/enumerations/view-modes/view-modes";
import PublicationRecord from "models/view-models/publication-record";
import UserPublicationFavoriteRecord from "models/view-models/user-publication-favorite-record";
import EmptyDashboardPublicationTab, {
    EmptyDashboardPublicationTabProps,
} from "organisms/dashboard-publications-tabs/empty-dashboard-publication-tab";
import { CollectionUtils } from "utilities/collection-utils";
import {
    PublicationSortItem,
    usePublicationSortItems,
} from "../../utilities/hooks/domain/publications/use-publication-sort-items";
import PublicationDisplayListItem from "./publications-display-list-item";
import { useFavoritePublicationsMapping } from "../../utilities/hooks/domain/user-publication-favorites/use-favorite-publications-mapping";
import SortableDragAndDropList from "../../organisms/sortable-drap-and-drop/sortable-drag-and-drop-list";
import { rectSortingStrategy } from "@dnd-kit/sortable";
import SortableDragAndDropItem from "../../organisms/sortable-drap-and-drop/sortable-drag-and-drop-item";
import { IconPositions } from "../../atoms/constants/icon-positions";

export interface PublicationsDisplayListProps
    extends EmptyDashboardPublicationTabProps {
    allowSorting?: boolean;
    favorites?: UserPublicationFavoriteRecord[];
    onChangeFavorites: () => void;
    onFavoritesReordered?: (favorites: UserPublicationFavoriteRecord[]) => void;
    publications: PublicationRecord[];
    view: ViewModes;
}

const CSS_BASE_CLASS = "c-publications-display-list";
const VIEW_MODE_TO_CLASS_NAME: Record<ViewModes, string> = {
    [ViewModes.Grid]: "grid",
    [ViewModes.List]: "list",
};
const VIEW_MODE_TO_ICON_POSITION: Record<ViewModes, IconPositions> = {
    [ViewModes.Grid]: IconPositions.Top,
    [ViewModes.List]: IconPositions.InnerLeft,
};

const PublicationsDisplayList = (props: PublicationsDisplayListProps) => {
    const {
        allowSorting = false,
        favorites = [],
        onChangeFavorites,
        onFavoritesReordered = () => {},
        publications,
        view = ViewModes.Grid,
    } = props;

    const { publicationSortItems, publicationsFromSortItems } =
        usePublicationSortItems(publications);
    const { favoritesFromPublications } =
        useFavoritePublicationsMapping(favorites);

    const handleOnSortChange = (updatedItems: PublicationSortItem[]) => {
        if (!allowSorting) {
            return;
        }

        const updatedPublications = publicationsFromSortItems(updatedItems);
        const updatedFavorites = favoritesFromPublications(updatedPublications);

        onFavoritesReordered(updatedFavorites);
    };

    if (CollectionUtils.isEmpty(publicationSortItems)) {
        return <EmptyDashboardPublicationTab {...props} />;
    }

    const listViewClass = VIEW_MODE_TO_CLASS_NAME[view];
    const listItemSortClass = allowSorting ? "-sortable" : "";

    return (
        <section className={`${CSS_BASE_CLASS} -${listViewClass}`}>
            <SortableDragAndDropList
                items={publicationSortItems}
                onSortChange={handleOnSortChange}
                strategy={rectSortingStrategy}>
                {publicationSortItems.map((publicationSortItem) => (
                    <SortableDragAndDropItem
                        allowIconOnlyDrag
                        enabled={allowSorting}
                        iconPosition={VIEW_MODE_TO_ICON_POSITION[view]}
                        id={publicationSortItem.id}
                        key={publicationSortItem.id}>
                        <PublicationDisplayListItem
                            cssClassModifier={listItemSortClass}
                            favorites={favorites}
                            key={publicationSortItem.id}
                            onChangeFavorites={onChangeFavorites}
                            publicationSortItem={publicationSortItem}
                            view={view}
                        />
                    </SortableDragAndDropItem>
                ))}
            </SortableDragAndDropList>
        </section>
    );
};

export default PublicationsDisplayList;
